import { z } from 'zod';

/**
 * Theming is token-based rather than CSS-based so the exact same theme record
 * drives the web portal (CSS custom properties), the desktop shell (window
 * chrome + tray) and the mobile app (React Native StyleSheet). Adding a colour
 * here makes it available everywhere without touching three codebases.
 *
 * An IT service provider usually resells the portal, so themes are also the
 * white-labelling mechanism: a theme can be bound to a customer, and that
 * customer's users see their own logo and palette.
 */

export const COLOR_MODES = ['light', 'dark', 'system'] as const;
export type ColorMode = (typeof COLOR_MODES)[number];

const hex = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'must be a hex colour');

/**
 * One palette per color mode. Semantic names only — never "blue500" — so a
 * rebrand is a data change, not a find-and-replace.
 */
export const paletteSchema = z.object({
  // surfaces, back to front
  bg: hex,
  surface: hex,
  surfaceRaised: hex,
  surfaceSunken: hex,
  overlay: hex,
  border: hex,
  borderStrong: hex,

  // text
  text: hex,
  textMuted: hex,
  textSubtle: hex,
  textInverted: hex,

  // brand
  primary: hex,
  primaryHover: hex,
  primaryText: hex,
  accent: hex,

  // health / severity — these map 1:1 onto HealthState and AlertSeverity so the
  // status grid, the alert list and the charts can never drift apart.
  up: hex,
  degraded: hex,
  down: hex,
  maintenance: hex,
  unknown: hex,

  info: hex,
  warning: hex,
  critical: hex,
  success: hex,

  /**
   * Categorical series colours for charts. Ordered; widgets take them in
   * sequence. Keep to 8 — beyond that, charts stop being readable anyway.
   */
  chart: z.array(hex).min(4).max(12),
});
export type Palette = z.infer<typeof paletteSchema>;

export const themeSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  /** Built-in themes ship with the server and cannot be deleted. */
  builtin: z.boolean().default(false),
  /** Bind to a customer for white-labelled tenant portals. null = available to all. */
  customerId: z.string().nullable().default(null),
  /** Shown in the theme picker; also the default for new users. */
  isDefault: z.boolean().default(false),

  light: paletteSchema,
  dark: paletteSchema,

  branding: z
    .object({
      productName: z.string().default('IT Resources'),
      logoUrl: z.string().nullable().default(null),
      logoDarkUrl: z.string().nullable().default(null),
      faviconUrl: z.string().nullable().default(null),
      /** Shown on the login screen. */
      tagline: z.string().default(''),
      supportEmail: z.string().nullable().default(null),
      supportPhone: z.string().nullable().default(null),
    })
    .default({
      productName: 'IT Resources',
      logoUrl: null,
      logoDarkUrl: null,
      faviconUrl: null,
      tagline: '',
      supportEmail: null,
      supportPhone: null,
    }),

  typography: z
    .object({
      fontSans: z.string().default("'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif"),
      fontMono: z.string().default("'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace"),
      /** Multiplier applied to the base 14px scale. NOC walls want 1.15+. */
      scale: z.number().min(0.75).max(1.6).default(1),
    })
    .default({
      fontSans: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontMono: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace",
      scale: 1,
    }),

  shape: z
    .object({
      radius: z.number().min(0).max(24).default(10),
      density: z.enum(['comfortable', 'compact']).default('comfortable'),
      /** Disable for NOC screens/projectors where shadows wash out. */
      shadows: z.boolean().default(true),
    })
    .default({ radius: 10, density: 'comfortable', shadows: true }),

  accessibility: z
    .object({
      /** Bumps contrast and thickens borders to meet WCAG AA on both modes. */
      highContrast: z.boolean().default(false),
      /** Adds shape/pattern encoding alongside colour on status tiles + charts. */
      colorBlindSafe: z.boolean().default(false),
      reduceMotion: z.boolean().default(false),
    })
    .default({ highContrast: false, colorBlindSafe: false, reduceMotion: false }),

  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Theme = z.infer<typeof themeSchema>;

export const themeCreateSchema = themeSchema
  .omit({ id: true, createdAt: true, updatedAt: true, builtin: true })
  .partial({ customerId: true, isDefault: true, branding: true, typography: true, shape: true, accessibility: true });
export type ThemeCreate = z.infer<typeof themeCreateSchema>;

/** Per-user selection, stored on the user record and mirrored to localStorage. */
export const themePreferenceSchema = z.object({
  themeId: z.string().nullable().default(null),
  mode: z.enum(COLOR_MODES).default('system'),
  /** Per-user overrides that win over the theme's own accessibility block. */
  highContrast: z.boolean().nullable().default(null),
  reduceMotion: z.boolean().nullable().default(null),
  density: z.enum(['comfortable', 'compact']).nullable().default(null),
  fontScale: z.number().min(0.75).max(1.6).nullable().default(null),
});
export type ThemePreference = z.infer<typeof themePreferenceSchema>;

/**
 * Flattens a theme + mode + user overrides into `--itr-*` custom properties.
 * Shared by the web portal and the desktop shell; the mobile app consumes the
 * same object as a plain token map.
 */
export function themeToTokens(
  theme: Theme,
  mode: 'light' | 'dark',
  overrides: Partial<ThemePreference> = {},
): Record<string, string> {
  const palette = mode === 'dark' ? theme.dark : theme.light;
  const density = overrides.density ?? theme.shape.density;
  const scale = overrides.fontScale ?? theme.typography.scale;
  const highContrast = overrides.highContrast ?? theme.accessibility.highContrast;

  const tokens: Record<string, string> = {};
  for (const [key, value] of Object.entries(palette)) {
    if (key === 'chart') continue;
    tokens[`--itr-${kebab(key)}`] = value as string;
  }
  palette.chart.forEach((color, i) => {
    tokens[`--itr-chart-${i + 1}`] = color;
  });

  tokens['--itr-font-sans'] = theme.typography.fontSans;
  tokens['--itr-font-mono'] = theme.typography.fontMono;
  tokens['--itr-font-size'] = `${(14 * scale).toFixed(2)}px`;
  tokens['--itr-radius'] = `${theme.shape.radius}px`;
  tokens['--itr-radius-sm'] = `${Math.max(2, theme.shape.radius - 4)}px`;
  tokens['--itr-radius-lg'] = `${theme.shape.radius + 6}px`;

  // Density drives one spacing unit; every component derives from it.
  tokens['--itr-space'] = density === 'compact' ? '6px' : '8px';
  tokens['--itr-row-h'] = density === 'compact' ? '28px' : '36px';
  tokens['--itr-pad'] = density === 'compact' ? '10px' : '16px';

  tokens['--itr-shadow'] = theme.shape.shadows
    ? mode === 'dark'
      ? '0 1px 2px rgba(0,0,0,.5), 0 8px 24px rgba(0,0,0,.35)'
      : '0 1px 2px rgba(16,24,40,.06), 0 8px 24px rgba(16,24,40,.08)'
    : 'none';

  tokens['--itr-border-w'] = highContrast ? '2px' : '1px';
  tokens['--itr-focus-ring'] = highContrast ? `0 0 0 3px ${palette.accent}` : `0 0 0 2px ${palette.accent}66`;

  const motion = (overrides.reduceMotion ?? theme.accessibility.reduceMotion) ? '0ms' : '160ms';
  tokens['--itr-motion'] = motion;

  return tokens;
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

/** Health/severity token lookup used by widgets in every client. */
export function healthToken(health: string): string {
  switch (health) {
    case 'up':
      return 'var(--itr-up)';
    case 'degraded':
      return 'var(--itr-degraded)';
    case 'down':
      return 'var(--itr-down)';
    case 'maintenance':
      return 'var(--itr-maintenance)';
    default:
      return 'var(--itr-unknown)';
  }
}

export function intentToken(intent: string | undefined): string {
  switch (intent) {
    case 'good':
      return 'var(--itr-success)';
    case 'warn':
      return 'var(--itr-warning)';
    case 'bad':
      return 'var(--itr-critical)';
    default:
      return 'var(--itr-primary)';
  }
}
